import { Box, Divider, Grid, IconButton, InputAdornment, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import theme from "../../theme";
import { Add, BorderColor, Close, DeleteForever, Description, Search, VisibilityOutlined } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import Dropzone from "react-dropzone";
import { createMeeting, deleteMeeting, getMeetingList, updateMeeting } from "../../api/adminMeetingApi";
import CustomTimePicker from "../../common/components/CustomTimePicker";
import moment from "moment";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto',
    maxHeight: '400px',
    overflow: 'auto',
}));

// ---------- Validation helpers ----------
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{3,}$/;
// Title / Location: must contain at least one letter, no purely numeric input.
// Allow letters, spaces, and common punctuation; reject if no alphabet present.
const HAS_LETTER_REGEX = /[A-Za-z]/;
const NUMERIC_ONLY_REGEX = /^\d+$/;
// Contact Name: letters, spaces, dots, apostrophes, hyphens only (no digits)
const NAME_REGEX = /^[A-Za-z][A-Za-z\s.'-]*$/;

const validateField = (name, value) => {
    switch (name) {
        case 'title': {
            if (!value || !value.trim()) return 'Title is required';
            if (NUMERIC_ONLY_REGEX.test(value.trim())) return 'Title cannot contain only numbers';
            if (!HAS_LETTER_REGEX.test(value)) return 'Please enter a valid title';
            if (value.trim().length < 3) return 'Title must be at least 3 characters';
            return '';
        }
        case 'short_description': {
            if (!value || !value.trim()) return 'Description is required';
            return '';
        }
        case 'location': {
            if (!value || !value.trim()) return 'Meeting Location is required';
            if (NUMERIC_ONLY_REGEX.test(value.trim())) return 'Location cannot contain only numbers';
            if (!HAS_LETTER_REGEX.test(value)) return 'Please enter a valid location';
            return '';
        }
        case 'meeting_date': {
            if (!value) return 'Meeting Date is required';
            return '';
        }
        case 'meeting_time': {
            if (!value) return 'Meeting Time is required';
            return '';
        }
        case 'person_name': {
            if (!value || !value.trim()) return 'Contact Name is required';
            if (!NAME_REGEX.test(value.trim())) return 'Contact Name can only contain letters';
            return '';
        }
        case 'person_email': {
            if (!value || !value.trim()) return 'Contact Email is required';
            if (!EMAIL_REGEX.test(value.trim())) return 'Please enter a valid email address';
            return '';
        }
        case 'person_phoneno': {
            if (!value) return 'Phone Number is required';
            // Mirror the pattern from your shared snippet:
            // value typically arrives as "+<country><number>", e.g. "+919876543210"
            if (value.includes('91') ? value.length < 12 : value.length < 11) {
                return 'Please enter a valid phone number';
            }
            return '';
        }
        default:
            return '';
    }
};

const ManageMeeting = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [addOpen, setAddOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [meetings, setMeetings] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [editingMeetings, setEditingMeetings] = useState(null);
    const [clearSearch, setClearSearch] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [errors, setErrors] = useState({});
    const [newMeeting, setNewMeeting] = useState({
        title: '',
        short_description: '',
        meeting_date: '',
        meeting_time: '',
        thumbnail: [],
        location: '',
        person_name: '',
        person_email: '',
        person_phoneno: ''
    });

    const getMeetings = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getMeetingList(pageNo + 1, pageSize, searchText);
            setMeetings(response.data);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        getMeetings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNo, pageSize, clearSearch]);

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const handleDelete = async (id) => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await deleteMeeting(id);
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({ loading: false }));
                setDeleteConfirm(null);
                getMeetings();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const handleOpenDialog = async (blog = null) => {
        setEditingMeetings(blog);
        setErrors({});
        dispatch(setLoading({ loading: true }));
        const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${blog?.image_url?.url}&download=false`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const blob = await responseData.blob();
        const imageUrl = URL.createObjectURL(blob);
        setNewMeeting({
            title: blog.title,
            person_name: blog.contact_name,
            person_email: blog.contact_email,
            person_phoneno: blog.contact_number,
            location: blog.location,
            short_description: blog.description,
            thumbnail: blog.image_url,
            thumbnail_url: imageUrl,
            meeting_date: blog?.meeting_datetime ? moment.utc(blog?.meeting_datetime).format('YYYY-MM-DD') : '',
            meeting_time: blog?.meeting_datetime ? moment.utc(blog?.meeting_datetime).format('HH:mm') : '',
        });
        dispatch(setLoading({ loading: false }));
        setAddOpen(true);
    };

    const handleCloseDialog = () => {
        setEditingMeetings(null);
        setViewOpen(false);
        setErrors({});
        setNewMeeting({
            title: '',
            short_description: '',
            meeting_date: '',
            meeting_time: '',
            thumbnail: [],
            location: '',
            person_name: '',
            person_email: '',
            person_phoneno: ''
        });
        setAddOpen(false);
    };

    // Unified change handler that updates state AND validates
    const handleFieldChange = (name, value) => {
        setNewMeeting((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    };

    // Validate all fields at submit time
    const validateAll = () => {
        const fieldsToValidate = [
            'title',
            'short_description',
            'location',
            'meeting_date',
            'meeting_time',
            'person_name',
            'person_email',
            'person_phoneno',
        ];
        const newErrors = {};
        fieldsToValidate.forEach((field) => {
            const err = validateField(field, newMeeting[field]);
            if (err) newErrors[field] = err;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveBlog = async () => {
        if (!validateAll()) {
            showNotification('Please fix the errors before submitting', 'error');
            return;
        }
        try {
            dispatch(setLoading({ loading: true }));
            let response;
            const formData = new FormData();
            formData.append('title', newMeeting.title);
            formData.append('description', newMeeting.short_description);
            formData.append('location', newMeeting.location);
            formData.append('contact_name', newMeeting.person_name);
            formData.append('contact_email', newMeeting.person_email);
            formData.append('contact_number', newMeeting.person_phoneno);
            formData.append('meeting_datetime', new Date(`${newMeeting.meeting_date}T${newMeeting?.meeting_time}:00Z`).toISOString());
            if (Array.isArray(newMeeting?.thumbnail) && newMeeting?.thumbnail?.length > 0) {
                formData.append('image', newMeeting.thumbnail[0]);
            } else if (meetings?.thumbnail) {
                formData.append('thumbnail_url', newMeeting.thumbnail);
            }
            if (editingMeetings) {
                response = await updateMeeting(editingMeetings.meeting_id, formData);
            } else {
                response = await createMeeting(formData);
            }
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({ loading: false }));
                getMeetings();
            }
            handleCloseDialog();
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const handleDrop = (acceptedFiles) => {
        setNewMeeting((prev) => ({
            ...prev,
            thumbnail: acceptedFiles?.map((files) => files)
        }));
    };

    // Disable Save when any required field is empty OR any error is present
    const hasErrors = Object.values(errors).some((e) => !!e);
    const isFormIncomplete =
        !newMeeting.title?.trim() ||
        !newMeeting.person_name?.trim() ||
        !newMeeting.person_email?.trim() ||
        !newMeeting.person_phoneno ||
        !newMeeting.short_description?.trim() ||
        !newMeeting.meeting_date ||
        !newMeeting.meeting_time ||
        !newMeeting.location?.trim();

    return (
        <Fragment>
            {loading && (<LoadingSpinner />)}
            <CommonDialog
                open={!!deleteConfirm}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to permanently delete this meeting??
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                handleDelete(deleteConfirm.meeting_id);
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteConfirm(null);
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonDialog
                open={addOpen}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                                {viewOpen ? 'View Meeting' : editingMeetings ? 'Edit Meeting' : 'Add New Meeting'}
                            </Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => { handleCloseDialog(); }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Title *"
                                name="title"
                                value={newMeeting.title}
                                onChange={(e) => handleFieldChange('title', e.target.value)}
                                placeholder="Enter the title"
                                disabled={viewOpen}
                                error={!!errors.title}
                                helperText={errors.title}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Description *"
                                name="short_description"
                                value={newMeeting.short_description}
                                onChange={(e) => handleFieldChange('short_description', e.target.value)}
                                placeholder="Enter the Description"
                                disabled={viewOpen}
                                multiline
                                error={!!errors.short_description}
                                helperText={errors.short_description}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Meeting Location *"
                                name="location"
                                value={newMeeting.location}
                                onChange={(e) => handleFieldChange('location', e.target.value)}
                                placeholder="Enter the Meeting Location"
                                disabled={viewOpen}
                                error={!!errors.location}
                                helperText={errors.location}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommonFormField
                                label="Date *"
                                type="date"
                                name="eventdate"
                                value={newMeeting.meeting_date}
                                onChange={(e) => handleFieldChange('meeting_date', e.target.value)}
                                placeholder="Meeting Date"
                                disabled={viewOpen}
                                error={!!errors.meeting_date}
                                helperText={errors.meeting_date}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomTimePicker
                                label="Time *"
                                value={newMeeting.meeting_time}
                                onChange={(e) => handleFieldChange('meeting_time', e.target.value)}
                                name="eventtime"
                                placeholder="Time"
                                format="24h"
                                disabled={viewOpen}
                                error={!!errors.meeting_time}
                                helperText={errors.meeting_time}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Contact Name *"
                                name="contact_name"
                                value={newMeeting.person_name}
                                onChange={(e) => handleFieldChange('person_name', e.target.value)}
                                placeholder="Enter the Contact Name"
                                disabled={viewOpen}
                                error={!!errors.person_name}
                                helperText={errors.person_name}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Contact Email *"
                                name="contact_email"
                                value={newMeeting.person_email}
                                onChange={(e) => handleFieldChange('person_email', e.target.value)}
                                placeholder="Enter the Contact Email"
                                disabled={viewOpen}
                                error={!!errors.person_email}
                                helperText={errors.person_email}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Phone Number *"
                                name="phone"
                                value={newMeeting.person_phoneno}
                                onChange={(e) => {
                                    const phoneVal = e?.phone ?? '';
                                    handleFieldChange('person_phoneno', phoneVal);
                                }}
                                placeholder="Enter phone number"
                                isPhone={true}
                                disabled={viewOpen}
                                error={!!errors.person_phoneno}
                                helperText={errors.person_phoneno}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <Box sx={{ width: '100%' }}>
                                {newMeeting.thumbnail_url ? (
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <img
                                            src={newMeeting.thumbnail_url}
                                            height="150px"
                                            width="150px"
                                            style={{ display: 'block' }}
                                            alt="thumbnail"
                                        />
                                        {!viewOpen && (
                                            <IconButton
                                                size="small"
                                                onClick={() => setNewMeeting((prev) => ({
                                                    ...prev,
                                                    thumbnail: [],
                                                    thumbnail_url: ''
                                                }))}
                                                color="error"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                                    padding: '4px',
                                                    minWidth: 'auto',
                                                    minHeight: 'auto'
                                                }}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                ) : newMeeting?.thumbnail?.length > 0 ? (
                                    <Box sx={{ mt: 2 }}>
                                        {newMeeting?.thumbnail?.map((file, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    backgroundColor: 'background.paper'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Description color="primary" />
                                                    <Box>
                                                        <Typography variant="body1">{file.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(file.size / 1024).toFixed(2)} KB
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setNewMeeting((prev) => ({
                                                        ...prev,
                                                        thumbnail: newMeeting.thumbnail.splice(index, -1)
                                                    }))}
                                                    color="error"
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : !viewOpen && (
                                    <Dropzone
                                        onDrop={handleDrop}
                                        accept={{
                                            'image/jpeg': ['.jpg', '.jpeg'],
                                            'image/png': ['.png']
                                        }}
                                        maxFiles={1}
                                    >
                                        {({ getRootProps, getInputProps, isDragActive }) => (
                                            <Box
                                                {...getRootProps()}
                                                sx={{
                                                    border: '2px dashed',
                                                    borderColor: '#D8DAEB',
                                                    borderRadius: 2,
                                                    padding: { xs: 3, md: 6 },
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    minHeight: 100,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <input {...getInputProps()} />
                                                <img src="/Images/sound.png" alt="upload" />
                                                <Typography fontWeight={600} gutterBottom>
                                                    Drag and drop your files
                                                </Typography>
                                                <CommonButton
                                                    label="Browse"
                                                    color="Black"
                                                    background="White"
                                                    border={`1px solid ${theme.palette.background.secondary}`}
                                                    width="100px"
                                                    height="40px"
                                                    marginBottom="0px"
                                                    marginTop="10px"
                                                />
                                            </Box>
                                        )}
                                    </Dropzone>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <Fragment>
                        {!viewOpen && (
                            <CommonButton
                                label="Save"
                                size="small"
                                background={theme.palette.secondary.main}
                                color={theme.palette.background.main}
                                fontWeight={700}
                                marginBottom="10px"
                                disabled={isFormIncomplete || hasErrors}
                                handleClick={() => { handleSaveBlog(); }}
                            />
                        )}
                    </Fragment>
                )}
            />
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={600}>Manage Meetings</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                    <TextField
                        placeholder="Search By Meeting Title Or Contact Person"
                        variant="outlined"
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: theme.palette.background.main,
                                '&:hover fieldset': { border: '1px solid lightgray' },
                                '&.Mui-focused fieldset': { border: '1px solid lightgray' },
                            },
                            '& input': {
                                height: '24px',
                                fontSize: '16px',
                                fontWeight: 500,
                                width: '320px'
                            }
                        }}
                        onChange={(e) => { setSearchText(e.target.value); }}
                        value={searchText}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 30, color: theme.palette.background.dark }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <CommonButton
                        label={clearSearch ? "Clear" : "Search"}
                        marginBottom="0px"
                        width="120px"
                        fontWeight={600}
                        value={searchText}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={() => {
                            if (clearSearch) { setSearchText(''); }
                            setClearSearch(!clearSearch);
                            setPageNo(0);
                            setMeetings(null);
                        }}
                    />
                    <CommonButton
                        label="Add Meeting"
                        marginBottom="0px"
                        width="150px"
                        fontWeight={600}
                        startIcon={<Add />}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={() => { setErrors({}); setAddOpen(true); }}
                    />
                </Box>
            </Box>
            <Box pt={1} pb={0}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell>Meeting Title</Cell>
                                <Cell>Meeting Time</Cell>
                                <Cell>Location</Cell>
                                <Cell>Contact Person</Cell>
                                <Cell>Status</Cell>
                                <Cell>Created Date</Cell>
                                <Cell>Actions</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {meetings?.data.length > 0 ? meetings?.data?.map((blog) => (
                                <TableRow key={blog.meeting_id}>
                                    <InnerCell style={{ color: 'black' }}>{blog.title}</InnerCell>
                                    <InnerCell style={{ color: 'black' }}>
                                        {blog.meeting_datetime ? moment.utc(blog?.meeting_datetime)?.format('DD, MMM YYYY HH:mm A') : ''}
                                    </InnerCell>
                                    <InnerCell style={{ color: 'black' }}>{blog.location}</InnerCell>
                                    <InnerCell>{blog.contact_name}</InnerCell>
                                    <InnerCell>{blog.status}</InnerCell>
                                    <InnerCell>{moment(blog?.created_at).format('DD, MMM YYYY')}</InnerCell>
                                    <InnerCell>
                                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(blog)}>
                                            <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(blog)}>
                                            <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                handleOpenDialog(blog);
                                                setViewOpen(true);
                                            }}
                                        >
                                            <VisibilityOutlined sx={{ color: theme.palette.secondary.main }} />
                                        </IconButton>
                                    </InnerCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography>No Meeting Scheduled</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Box>

            <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                    component="div"
                    count={meetings?.total || 0}
                    rowsPerPage={pageSize}
                    page={pageNo}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </Box>
        </Fragment>
    );
};

export default ManageMeeting;